// frontend/src/pages/SettingsNotifications.jsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'react-toastify';
import axiosAuth from '../axiosConfig';
import TelegramSettingsCard from '../components/TelegramSettingsCard';

/* --------------------------- small helpers --------------------------- */
const classCard = 'card';

const Section = ({ title, subtitle, children, right }) => (
  <section
    className={`${classCard} p-4 sm:p-5 md:p-6 bg-white dark:bg-slate-800 rounded-2xl shadow border border-blue-100 dark:border-slate-700`}
  >
    <div className="mb-3 sm:mb-4 flex items-start justify-between gap-3">
      <div>
        <h2 className="text-base sm:text-lg md:text-xl font-semibold text-blue-800 dark:text-slate-100">
          {title}
        </h2>
        {subtitle && (
          <p className="text-xs sm:text-sm text-blue-600 dark:text-slate-300 mt-1">
            {subtitle}
          </p>
        )}
      </div>
      {right ? <div className="shrink-0">{right}</div> : null}
    </div>
    {children}
  </section>
);

/* --------------------------- defaults --------------------------- */
const defaultSettings = {
  theme: 'system', // 'light' | 'dark' | 'system'
  language: 'en-IN',
  currency: 'INR',
  timezone: 'auto', // 'auto' or IANA TZ like 'Asia/Kolkata'
  notifications: {
    muteAll: false,
    accountSecurity: { app: true, email: true, tg: false },
    systemAnnouncements: { app: true, email: false, tg: false },
    priceAlerts: { app: true, email: false, tg: false },
    ordersSpot: { app: true, email: true, tg: false },
    p2pTrades: { app: true, email: false, tg: true },
    fundingDeposits: { app: true, email: true, tg: false },
    fundingWithdrawals: { app: true, email: true, tg: false },
    messages: { app: true, email: false, tg: false },
  },
  privacy: {
    showPublicProfile: true,
    showActivity: false,
    allowMessagesFrom: 'anyone',
  },
};

/* --------------------------- utils --------------------------- */
function deepMerge(base, extra) {
  if (!extra || typeof extra !== 'object') return base;
  const out = Array.isArray(base) ? base.slice() : { ...base };
  for (const k of Object.keys(extra)) {
    if (Array.isArray(extra[k])) out[k] = extra[k].slice();
    else if (extra[k] && typeof extra[k] === 'object')
      out[k] = deepMerge(base[k] || {}, extra[k]);
    else out[k] = extra[k];
  }
  return out;
}

/* --------------------------- backend prefs mapping --------------------------- */
const BUCKET_TO_GROUP = {
  accountSecurity: 'security',
  p2pTrades: 'p2p',
  messages: 'p2p',
  fundingDeposits: 'deposits',
  fundingWithdrawals: 'withdrawals',
};

function bucketDefaults(bucketKey) {
  // reasonable defaults if not in matrix/global
  if (bucketKey === 'p2pTrades') return { app: true, email: false, tg: true };
  if (bucketKey === 'messages') return { app: true, email: false, tg: false };
  if (bucketKey === 'fundingDeposits') return { app: true, email: true, tg: false };
  if (bucketKey === 'fundingWithdrawals') return { app: true, email: true, tg: false };
  if (bucketKey === 'accountSecurity') return { app: true, email: true, tg: false };
  return { app: true, email: false, tg: false };
}

function prefsToUI(notificationPrefs) {
  const prefs = notificationPrefs || {};
  const out = JSON.parse(JSON.stringify(defaultSettings));
  out.notifications.muteAll = Boolean(prefs.muteAll);

  const global = prefs.channels || {};
  const matrix = prefs.matrix || {};

  for (const bucketKey of Object.keys(out.notifications)) {
    if (bucketKey === 'muteAll') continue;
    const group = BUCKET_TO_GROUP[bucketKey];
    if (!group) continue;

    const def = bucketDefaults(bucketKey);
    const m = matrix[group] || null;

    const app = m ? (m.web ?? def.app) : (global.web ?? def.app);
    const email = m ? (m.email ?? def.email) : (global.email ?? def.email);
    const tg = m ? (m.telegram ?? def.tg) : (global.telegram ?? def.tg);

    out.notifications[bucketKey] = { app: Boolean(app), email: Boolean(email), tg: Boolean(tg) };
  }

  return out;
}

function uiToPrefs(notificationsState) {
  const n = notificationsState || {};
  const prefs = {
    muteAll: Boolean(n.muteAll),
    channels: { web: true, email: true, telegram: false },
    matrix: {},
    events: {
      deposits: { arrived: true, credited: true, needSenderDetails: true, complianceHold: true },
      withdrawals: { requested: true, approved: true, rejected: true, completed: true },
      p2p: { tradeEvents: true, messages: true, disputes: true },
      security: { login: true, passwordChange: true },
    },
  };

  const matrix = {};

  const setGroup = (group, v) => {
    if (!v) return;
    matrix[group] = {
      web: Boolean(v.app),
      email: Boolean(v.email),
      telegram: Boolean(v.tg),
    };
  };

  setGroup('security', n.accountSecurity);
  setGroup('p2p', {
    // merge p2pTrades + messages into one group; allow tg toggle from p2pTrades
    app: Boolean(n.p2pTrades?.app || n.messages?.app),
    email: Boolean(n.p2pTrades?.email || n.messages?.email),
    tg: Boolean(n.p2pTrades?.tg || n.messages?.tg),
  });
  setGroup('deposits', n.fundingDeposits);
  setGroup('withdrawals', n.fundingWithdrawals);

  prefs.matrix = matrix;

  // Derive global channels as OR of matrix channels (fallback if matrix missing)
  const anyWeb = Object.values(matrix).some((m) => m.web);
  const anyEmail = Object.values(matrix).some((m) => m.email);
  const anyTg = Object.values(matrix).some((m) => m.telegram);

  prefs.channels = { web: anyWeb, email: anyEmail, telegram: anyTg };

  return prefs;
}

function shallowEqual(a, b) {
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return false;
  }
}

function applyTheme(theme) {
  const root = document.documentElement;
  const sysDark =
    window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
  const mode = theme === 'system' ? (sysDark ? 'dark' : 'light') : theme;
  if (mode === 'dark') root.classList.add('dark');
  else root.classList.remove('dark');
  localStorage.setItem('theme', theme);
}

function detectLocalTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

function passwordStrengthHint(p) {
  if (!p) return '';
  let score = 0;
  if (p.length >= 10) score++;
  if (/[A-Z]/.test(p)) score++;
  if (/[a-z]/.test(p)) score++;
  if (/\d/.test(p)) score++;
  if (/[^A-Za-z0-9]/.test(p)) score++;
  const bars = ['Very weak', 'Weak', 'Okay', 'Good', 'Strong', 'Very strong'];
  return bars[Math.min(score, bars.length - 1)];
}

/* Pure WebAudio short beep — works without assets */
function playTestBeep() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = 'triangle';
    o.frequency.setValueAtTime(880, ctx.currentTime);
    g.gain.setValueAtTime(0.001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.2, ctx.currentTime + 0.01);
    g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.35);
    o.connect(g);
    g.connect(ctx.destination);
    o.start();
    o.stop(ctx.currentTime + 0.4);
  } catch {
    /* ignore */
  }
}

/* --------------------------- main component --------------------------- */
export default function SettingsNotifications() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(defaultSettings);

  const loadedFromServerRef = useRef(defaultSettings); // last loaded snapshot
  const [hasUnsaved, setHasUnsaved] = useState(false);

  // Security state
  const [pw, setPw] = useState({ current: '', next: '', confirm: '' });
  const [changingPw, setChangingPw] = useState(false);

  // Sessions
  const [sessions, setSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [revoking, setRevoking] = useState(false);

  // 🔻 NEW: Telegram unlink & refresh key
  const [unlinking, setUnlinking] = useState(false);
  const [tgKey, setTgKey] = useState(0);
  const unlinkTelegram = useCallback(async () => {
    if (!window.confirm('Unlink Telegram from your account? You can re-link later.')) return;
    setUnlinking(true);
    try {
      await axiosAuth.post('/notifications/telegram/unlink');
      toast.success('Telegram unlinked.');
      // Force-refresh both TelegramSettingsCard instances
      setTgKey((k) => k + 1);
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Failed to unlink Telegram');
    }
    setUnlinking(false);
  }, []);

  // System theme listener (for theme:'system')
  useEffect(() => {
    const mq =
      window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)');
    if (!mq) return;
    const handler = () => {
      const stored = localStorage.getItem('theme') || 'system';
      if (stored === 'system') applyTheme('system');
    };
    try {
      mq.addEventListener('change', handler);
    } catch {
      mq.addListener(handler);
    }
    return () => {
      try {
        mq.removeEventListener('change', handler);
      } catch {
        mq.removeListener(handler);
      }
    };
  }, []);

  // Load settings & sessions
  useEffect(() => {
    let mounted = true;

    (async () => {
      setLoading(true);
      try {
        const r = await axiosAuth.get('/settings');
        const serverPrefs = r?.data?.settings?.notificationPrefs || r?.data?.notificationPrefs || null;
        const merged = serverPrefs ? prefsToUI(serverPrefs) : defaultSettings;

        if (mounted) {
          setSettings(merged);
          loadedFromServerRef.current = merged;
          applyTheme(merged.theme);
          setHasUnsaved(false);
          // Persist a local cache so UI can work offline
          localStorage.setItem('settings.cache', JSON.stringify(merged));
        }
      } catch {
        // fallback to cached or theme from localStorage
        const cached = localStorage.getItem('settings.cache');
        if (cached) {
          const parsed = JSON.parse(cached);
          if (mounted) {
            setSettings(parsed);
            loadedFromServerRef.current = parsed;
          }
        }
        const t = localStorage.getItem('theme');
        if (t) applyTheme(t);
      }
      if (mounted) setLoading(false);
    })();

    (async () => {
      setLoadingSessions(true);
      try {
        const r = await axiosAuth.get('/auth/sessions');
        if (mounted)
          setSessions(Array.isArray(r?.data?.sessions) ? r.data.sessions : []);
      } catch {
        if (mounted) setSessions([]);
      }
      if (mounted) setLoadingSessions(false);
    })();

    return () => {
      mounted = false;
    };
  }, []);

  // Track dirty state
  useEffect(() => {
    setHasUnsaved(!shallowEqual(settings, loadedFromServerRef.current));
  }, [settings]);

  // Warn on unload if unsaved
  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (!hasUnsaved) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [hasUnsaved]);

  // Ctrl/Cmd+S to save
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        onSaveAll();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [settings, saving]);

  /* --------------------------- appearance --------------------------- */
  const onThemeChange = (value) => {
    setSettings((s) => ({ ...s, theme: value }));
    applyTheme(value);
  };

  /* --------------------------- prefs --------------------------- */
  const onPref = (key, value) => setSettings((s) => ({ ...s, [key]: value }));

  /* --------------------------- notifications --------------------------- */
  const buckets = useMemo(
    () => [
      { key: 'accountSecurity', label: 'Account & Security' },
      { key: 'ordersSpot', label: 'Spot/Convert Orders' },
      { key: 'p2pTrades', label: 'P2P Trades' },
      { key: 'fundingDeposits', label: 'Funding: Deposits' },
      { key: 'fundingWithdrawals', label: 'Funding: Withdrawals' },
      { key: 'messages', label: 'Messages & Chat' },
      { key: 'systemAnnouncements', label: 'System Announcements' },
      { key: 'priceAlerts', label: 'Price Alerts' },
    ],
    []
  );

  const channels = [
    { key: 'app', label: 'In-app' },
    { key: 'email', label: 'Email' },
    { key: 'tg', label: 'Telegram' },
  ];

  const toggleBucketChannel = (bucketKey, chKey) => {
    setSettings((s) => {
      if (s.notifications.muteAll) return s;
      return {
        ...s,
        notifications: {
          ...s.notifications,
          [bucketKey]: {
            ...s.notifications[bucketKey],
            [chKey]: !s.notifications[bucketKey][chKey],
          },
        },
      };
    });
  };

  const toggleMuteAll = () => {
    setSettings((s) => ({
      ...s,
      notifications: { ...s.notifications, muteAll: !s.notifications.muteAll },
    }));
  };

  /* --------------------------- saving --------------------------- */
  const onSaveAll = useCallback(async () => {
    if (!hasUnsaved) {
      toast.info('No changes to save');
      return;
    }
    setSaving(true);
    try {
      const notificationPrefs = uiToPrefs(settings.notifications);
      await axiosAuth.post('/settings', { notificationPrefs });
      // update snapshots
      loadedFromServerRef.current = settings;
      localStorage.setItem('settings.cache', JSON.stringify(settings));
      toast.success('Settings saved ✅');
      setHasUnsaved(false);
    } catch {
      toast.error('Could not save to server. Changes kept locally.');
      // keep local cache so user does not lose work
      localStorage.setItem('settings.cache', JSON.stringify(settings));
    }
    setSaving(false);
  }, [settings, hasUnsaved]);

  const onResetDefaults = () => {
    setSettings(defaultSettings);
    applyTheme(defaultSettings.theme);
    toast.info('Defaults restored (not saved yet)');
  };

  const onRevert = () => {
    setSettings(loadedFromServerRef.current);
    applyTheme(loadedFromServerRef.current.theme);
    toast.info('Reverted to last saved');
  };

  /* --------------------------- password --------------------------- */
  const changePassword = async (e) => {
    e.preventDefault();
    if (!pw.current || !pw.next || !pw.confirm)
      return toast.error('Please fill all password fields');
    if (pw.next !== pw.confirm)
      return toast.error('New passwords do not match');
    setChangingPw(true);
    try {
      await axiosAuth.post('/auth/change-password', {
        currentPassword: pw.current,
        newPassword: pw.next,
      });
      setPw({ current: '', next: '', confirm: '' });
      toast.success('Password changed');
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Password change failed');
    }
    setChangingPw(false);
  };

  /* --------------------------- sessions --------------------------- */
  const refreshSessions = async () => {
    setLoadingSessions(true);
    try {
      const r = await axiosAuth.get('/auth/sessions');
      setSessions(Array.isArray(r?.data?.sessions) ? r.data.sessions : []);
    } catch {
      setSessions([]);
    }
    setLoadingSessions(false);
  };

  const revokeOthers = async () => {
    setRevoking(true);
    try {
      await axiosAuth.post('/auth/sessions/revoke-others');
      toast.success('Logged out of other devices');
      await refreshSessions();
    } catch {
      toast.error('Failed to revoke other sessions');
    }
    setRevoking(false);
  };

  const isMuted = !!settings.notifications.muteAll;
  const detectedTz = detectLocalTimezone();
  const showDetected =
    settings.timezone === 'auto' ? detectedTz : settings.timezone;

  return (
    <motion.div
      className="min-h-screen bg-blue-50 dark:bg-slate-900 p-4 sm:p-5 md:p-6 text-[13px] sm:text-[14px] md:text-[16px]"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      {/* Local light/dark helpers (only affects this page if your global CSS doesn't define them) */}
      <style>{`
        .btn-primary{ @apply inline-flex items-center justify-center px-4 py-2 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 transition; }
        .btn-outline{ @apply inline-flex items-center justify-center px-3 py-2 rounded-lg border border-blue-200 text-blue-700 hover:bg-blue-50 dark:border-slate-700 dark:text-blue-300 dark:hover:bg-slate-900/70 transition; }
        .btn-danger{ @apply inline-flex items-center justify-center px-3 py-2 rounded-lg bg-red-600 text-white font-semibold hover:bg-red-700 transition; }
        .input, .select{ @apply w-full rounded-lg border bg-white text-blue-800 placeholder-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-400; }
        .checkbox{ @apply accent-blue-600; }
        @media (prefers-color-scheme: dark) {
          html.dark .input, html.dark .select { @apply bg-slate-900 border-slate-700 text-slate-100 placeholder-slate-500; }
        }
      `}</style>

      <div className="max-w-6xl mx-auto space-y-5 sm:space-y-6">
        {/* Header */}
        <header className="flex items-center justify-between gap-3">
          <div>
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-blue-800 dark:text-slate-100">
              Settings
              {hasUnsaved ? (
                <span className="ml-2 align-middle text-xs font-medium text-blue-800 dark:text-slate-100 border border-blue-300 dark:border-slate-600 px-2 py-1 rounded">
                  Unsaved
                </span>
              ) : null}
            </h1>
            <p className="text-xs sm:text-sm text-blue-600 dark:text-slate-300">
              Manage your account, security, preferences and notifications
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onRevert}
              disabled={!hasUnsaved || saving}
              className="btn-outline disabled:opacity-50"
            >
              Revert
            </button>
            <button
              onClick={onSaveAll}
              disabled={saving || !hasUnsaved}
              className="btn-primary disabled:opacity-60"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </header>

        {/* Appearance */}
        <Section
          title="Appearance"
          subtitle="Choose how the app looks on your device"
          right={
            <button type="button" onClick={onResetDefaults} className="btn-outline text-sm">
              Reset to defaults
            </button>
          }
        >
          <div className="flex flex-wrap gap-2 sm:gap-3">
            {['light', 'dark', 'system'].map((t) => (
              <label
                key={t}
                className={`cursor-pointer inline-flex items-center gap-2 px-3 py-2 rounded-xl border text-sm ${
                  settings.theme === t
                    ? 'border-blue-500 text-blue-800 dark:text-slate-100 bg-blue-50 dark:bg-slate-900'
                    : 'border-blue-200 dark:border-slate-700 text-blue-800 dark:text-slate-100 bg-white dark:bg-slate-800'
                }`}
              >
                <input
                  type="radio"
                  name="theme"
                  className="sr-only"
                  checked={settings.theme === t}
                  onChange={() => onThemeChange(t)}
                />
                <span className="capitalize">{t}</span>
              </label>
            ))}
          </div>
        </Section>

        {/* General Preferences */}
        <Section title="General" subtitle="Language, currency and time zone">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4">
            <div>
              <label className="block text-xs sm:text-sm font-medium text-blue-800 dark:text-slate-100 mb-1">
                Language
              </label>
              <select
                value={settings.language}
                onChange={(e) => onPref('language', e.target.value)}
                className="select h-9 sm:h-10 text-[13px] bg-white dark:bg-slate-900 border border-blue-200 dark:border-slate-700 text-blue-800 dark:text-slate-100"
              >
                <option value="en-IN">English (India)</option>
                <option value="en-US">English (US)</option>
                <option value="hi-IN">हिन्दी</option>
              </select>
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-medium text-blue-800 dark:text-slate-100 mb-1">
                Currency
              </label>
              <select
                value={settings.currency}
                onChange={(e) => onPref('currency', e.target.value)}
                className="select h-9 sm:h-10 text-[13px] bg-white dark:bg-slate-900 border border-blue-200 dark:border-slate-700 text-blue-800 dark:text-slate-100"
              >
                <option value="INR">INR ₹</option>
                <option value="USD">USD $</option>
                <option value="EUR">EUR €</option>
              </select>
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-medium text-blue-800 dark:text-slate-100 mb-1">
                Time zone
              </label>
              <div className="flex items-center gap-2">
                <select
                  value={settings.timezone}
                  onChange={(e) => onPref('timezone', e.target.value)}
                  className="select h-9 sm:h-10 text-[13px] bg-white dark:bg-slate-900 border border-blue-200 dark:border-slate-700 text-blue-800 dark:text-slate-100"
                >
                  <option value="auto">Auto-detect</option>
                  <option value="Asia/Kolkata">Asia/Kolkata (IST)</option>
                  <option value="UTC">UTC</option>
                </select>
                <button
                  type="button"
                  onClick={() => toast.info(`Detected timezone: ${detectedTz}`)}
                  className="btn-outline text-sm shrink-0"
                >
                  Detect now
                </button>
              </div>
              <p className="text-[11px] sm:text-xs text-blue-600 dark:text-slate-300 mt-1">
                Currently using: <span className="font-medium">{showDetected}</span>
              </p>
            </div>
          </div>
        </Section>

        {/* Notifications */}
        <Section title="Notifications" subtitle="Pick what we notify you about and where">
          <div className="flex items-center justify-between bg-blue-50/60 dark:bg-slate-900 border border-blue-100 dark:border-slate-700 rounded-xl p-3 sm:p-4 mb-3 sm:mb-4">
            <div>
              <p className="font-medium text-blue-800 dark:text-slate-100 text-sm">
                Mute all notifications
              </p>
              <p className="text-[11px] sm:text-xs text-blue-600 dark:text-slate-300">
                You can still view updates in-app
              </p>
            </div>
            <label className="inline-flex items-center">
              <input
                type="checkbox"
                className="checkbox h-4 w-4 sm:h-5 sm:w-5 accent-blue-600"
                checked={isMuted}
                onChange={toggleMuteAll}
              />
            </label>
          </div>

          {/* Mobile list layout */}
          <div className="md:hidden space-y-2">
            {buckets.map((b) => (
              <div
                key={b.key}
                className={`bg-blue-50/60 dark:bg-slate-900 border border-blue-100 dark:border-slate-700 rounded-xl p-3 ${
                  isMuted ? 'opacity-60' : ''
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="text-blue-800 dark:text-slate-100 text-sm font-medium">
                    {b.label}
                  </div>
                </div>
                <div className="mt-2 grid grid-cols-3 gap-2">
                  {channels.map((ch) => (
                    <label
                      key={ch.key}
                      className="flex items-center gap-1.5 text-[12px] text-blue-800 dark:text-slate-100"
                    >
                      <input
                        type="checkbox"
                        disabled={isMuted}
                        className="checkbox h-4 w-4 accent-blue-600"
                        checked={!!settings.notifications?.[b.key]?.[ch.key]}
                        onChange={() => toggleBucketChannel(b.key, ch.key)}
                      />
                      <span>{ch.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Desktop table layout */}
          <div className="hidden md:block overflow-x-auto">
            <table className={`min-w-full ${isMuted ? 'opacity-60' : ''}`}>
              <thead>
                <tr className="bg-blue-50/70 dark:bg-slate-900/60">
                  <th className="py-2 pr-2 text-left text-sm font-medium text-blue-800 dark:text-slate-100">
                    Category
                  </th>
                  <th className="py-2 px-2 text-left text-sm font-medium text-blue-800 dark:text-slate-100">
                    In-app
                  </th>
                  <th className="py-2 px-2 text-left text-sm font-medium text-blue-800 dark:text-slate-100">
                    Email
                  </th>
                  <th className="py-2 px-2 text-left text-sm font-medium text-blue-800 dark:text-slate-100">
                    Telegram
                  </th>
                </tr>
              </thead>
              <tbody>
                {buckets.map((b) => (
                  <tr
                    key={b.key}
                    className="border-b border-blue-100 dark:border-slate-700 last:border-b-0 hover:bg-blue-50/40 dark:hover:bg-slate-900/60 transition"
                  >
                    <td className="py-2 pr-2 text-blue-800 dark:text-slate-100">
                      {b.label}
                    </td>
                    {['app', 'email', 'tg'].map((ch) => (
                      <td key={ch} className="py-2 px-2">
                        <input
                          type="checkbox"
                          disabled={isMuted}
                          className="checkbox h-5 w-5 accent-blue-600"
                          checked={!!settings.notifications?.[b.key]?.[ch]}
                          onChange={() => toggleBucketChannel(b.key, ch)}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Inline tools */}
          <div className="mt-3 sm:mt-4 flex flex-wrap items-center gap-2">
            <button type="button" onClick={playTestBeep} className="btn-outline text-sm">
              Test in-app sound (client-only)
            </button>
            <button
              type="button"
              onClick={() => toast.success('This is a sample in-app notification')}
              className="btn-outline text-sm"
            >
              Test in-app toast (client-only)
            </button>
          </div>

          {/* Existing Telegram card */}
          <div className="mt-3 sm:mt-4">
            <TelegramSettingsCard key={tgKey} />
            {/* 🔻 NEW: Unlink button */}
            <div className="mt-2">
              <button
                type="button"
                onClick={unlinkTelegram}
                disabled={unlinking}
                className="btn-danger text-sm disabled:opacity-60"
              >
                {unlinking ? 'Unlinking…' : 'Unlink Telegram'}
              </button>
            </div>
          </div>
        </Section>

        {/* Security */}
        <Section
          title="Security"
          subtitle="Password, sessions and protections"
          right={
            <button type="button" onClick={refreshSessions} className="btn-outline text-sm">
              Refresh sessions
            </button>
          }
        >
          <form
            onSubmit={changePassword}
            className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4"
          >
            <div>
              <label className="block text-xs sm:text-sm font-medium text-blue-800 dark:text-slate-100 mb-1">
                Current password
              </label>
              <input
                type="password"
                className="input h-9 sm:h-10 text-[13px] bg-white dark:bg-slate-900 border border-blue-200 dark:border-slate-700 text-blue-800 dark:text-slate-100 placeholder:text-blue-400 dark:placeholder:text-slate-500"
                value={pw.current}
                onChange={(e) => setPw((x) => ({ ...x, current: e.target.value }))}
                autoComplete="current-password"
              />
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium text-blue-800 dark:text-slate-100 mb-1">
                New password
              </label>
              <input
                type="password"
                className="input h-9 sm:h-10 text-[13px] bg-white dark:bg-slate-900 border border-blue-200 dark:border-slate-700 text-blue-800 dark:text-slate-100 placeholder:text-blue-400 dark:placeholder:text-slate-500"
                value={pw.next}
                onChange={(e) => setPw((x) => ({ ...x, next: e.target.value }))}
                autoComplete="new-password"
              />
              <p className="text-[11px] sm:text-xs text-blue-600 dark:text-slate-300 mt-1">
                Strength:{' '}
                <span className="font-medium">
                  {passwordStrengthHint(pw.next) || '—'}
                </span>
              </p>
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium text-blue-800 dark:text-slate-100 mb-1">
                Confirm new password
              </label>
              <input
                type="password"
                className="input h-9 sm:h-10 text-[13px] bg-white dark:bg-slate-900 border border-blue-200 dark:border-slate-700 text-blue-800 dark:text-slate-100 placeholder:text-blue-400 dark:placeholder:text-slate-500"
                value={pw.confirm}
                onChange={(e) => setPw((x) => ({ ...x, confirm: e.target.value }))}
                autoComplete="new-password"
              />
            </div>
            <div className="md:col-span-3 flex flex-wrap items-center gap-2">
              <button type="submit" disabled={changingPw} className="btn-primary disabled:opacity-60">
                {changingPw ? 'Changing…' : 'Change password'}
              </button>
              <a href="/settings/security/2fa" className="btn-outline">
                Set up 2FA
              </a>
            </div>
          </form>

          <div className="mt-4 sm:mt-6">
            <h3 className="font-semibold text-blue-800 dark:text-slate-100 mb-2 text-sm sm:text-base">
              Active sessions
            </h3>
            <div className="bg-white dark:bg-slate-900 border border-blue-100 dark:border-slate-700 rounded-xl overflow-hidden">
              {loadingSessions ? (
                <div className="p-3 text-sm text-blue-600 dark:text-slate-300">
                  Loading sessions…
                </div>
              ) : sessions.length === 0 ? (
                <div className="p-3 text-sm text-blue-600 dark:text-slate-300">
                  No other active sessions.
                </div>
              ) : (
                <ul className="divide-y divide-blue-100 dark:divide-slate-700">
                  {sessions.map((s) => (
                    <li key={s.id || s.sessionId} className="p-3 text-[13px] sm:text-sm">
                      <div className="flex items-center justify-between">
                        <div className="min-w-0">
                          <div className="text-blue-800 dark:text-slate-100 truncate">
                            {s.device || 'Unknown device'} • {s.browser || s.ua || 'Browser'}
                          </div>
                          <div className="text-blue-600 dark:text-slate-400 text-[11px] sm:text-xs truncate">
                            {(s.ip || 'IP n/a')} • {(s.location || 'Location n/a')} •{' '}
                            {s.createdAt ? new Date(s.createdAt).toLocaleString() : '—'}
                          </div>
                        </div>
                        {s.current ? (
                          <span className="text-[11px] sm:text-xs px-2 py-1 rounded border border-blue-300 dark:border-slate-600 text-blue-800 dark:text-slate-100">
                            Current
                          </span>
                        ) : null}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <button
              onClick={revokeOthers}
              disabled={revoking}
              className="mt-3 btn-danger disabled:opacity-60"
            >
              {revoking ? 'Revoking…' : 'Log out of other devices'}
            </button>
          </div>
        </Section>

        {/* Privacy */}
        <Section title="Privacy" subtitle="Control what others can see">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4">
            <label className="inline-flex items-start gap-2 sm:gap-3">
              <input
                type="checkbox"
                className="mt-1 h-4 w-4 sm:h-5 sm:w-5 checkbox accent-blue-600"
                checked={settings.privacy.showPublicProfile}
                onChange={(e) =>
                  setSettings((s) => ({
                    ...s,
                    privacy: { ...s.privacy, showPublicProfile: e.target.checked },
                  }))
                }
              />
              <span>
                <span className="font-medium text-blue-800 dark:text-slate-100 text-sm">
                  Show public profile
                </span>
                <p className="text-[11px] sm:text-xs text-blue-600 dark:text-slate-300">
                  Allow others to view your public profile.
                </p>
              </span>
            </label>

            <label className="inline-flex items-start gap-2 sm:gap-3">
              <input
                type="checkbox"
                className="mt-1 h-4 w-4 sm:h-5 sm:w-5 checkbox accent-blue-600"
                checked={settings.privacy.showActivity}
                onChange={(e) =>
                  setSettings((s) => ({
                    ...s,
                    privacy: { ...s.privacy, showActivity: e.target.checked },
                  }))
                }
              />
              <span>
                <span className="font-medium text-blue-800 dark:text-slate-100 text-sm">
                  Show recent activity
                </span>
                <p className="text-[11px] sm:text-xs text-blue-600 dark:text-slate-300">
                  Display basic public activity (if available).
                </p>
              </span>
            </label>

            <div>
              <label className="block text-xs sm:text-sm font-medium text-blue-800 dark:text-slate-100 mb-1">
                Allow messages from
              </label>
              <select
                value={settings.privacy.allowMessagesFrom}
                onChange={(e) =>
                  setSettings((s) => ({
                    ...s,
                    privacy: { ...s.privacy, allowMessagesFrom: e.target.value },
                  }))
                }
                className="select h-9 sm:h-10 text-[13px] bg-white dark:bg-slate-900 border border-blue-200 dark:border-slate-700 text-blue-800 dark:text-slate-100"
              >
                <option value="anyone">Anyone</option>
                <option value="verified">Verified users only</option>
                <option value="none">No one</option>
              </select>
            </div>
          </div>
        </Section>

        {/* Connected Apps */}
        <Section title="Connected Apps" subtitle="Manage integrations and sign-in options">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
            <TelegramSettingsCard key={`apps-${tgKey}`} />
            <div className="bg-white dark:bg-slate-900 border border-blue-100 dark:border-slate-700 rounded-xl p-3 sm:p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-semibold text-blue-800 dark:text-slate-100 text-sm sm:text-base">
                    Google Sign-In
                  </h4>
                  <p className="text-[11px] sm:text-xs text-blue-600 dark:text-slate-300">
                    Use your Google account to sign in.
                  </p>
                </div>
                <button
                  type="button"
                  className="btn-outline text-sm"
                  onClick={() => toast.info('Wire this to your Google OAuth flow')}
                >
                  Connect / Manage
                </button>
              </div>
            </div>
          </div>
        </Section>

        {/* Danger Zone */}
        <Section title="Danger Zone">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div>
              <p className="font-semibold text-blue-800 dark:text-slate-100 text-sm sm:text-base">
                Delete account
              </p>
              <p className="text-[11px] sm:text-xs text-blue-600 dark:text-slate-300">
                Permanently remove your account and data after a cooling-off period.
              </p>
            </div>
            <a href="/account/delete" className="btn-danger">
              Delete my account
            </a>
          </div>
        </Section>

        {/* Bottom Save (mobile friendly) */}
        <div className="sticky bottom-3 flex justify-end">
          <button
            onClick={onSaveAll}
            disabled={saving || !hasUnsaved}
            className="btn-primary rounded-xl shadow-lg disabled:opacity-60"
          >
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>

      {loading && (
        <div className="fixed inset-0 bg-white/70 dark:bg-slate-900/70 flex items-center justify-center z-40">
          <div className="animate-spin h-8 w-8 rounded-full border-2 border-blue-600 dark:border-blue-400 border-t-transparent" />
        </div>
      )}
    </motion.div>
  );
}
